import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { api, ApiError, can, type Me, type ProjectSummary } from "./api";
import { Icon, Mark, Person } from "./components/ui";
import { Audit } from "./pages/Audit";
import { CostsPage } from "./pages/Costs";
import { DocsPage } from "./pages/Docs";
import { AlertsPage, useAlerts } from "./pages/Alerts";
import { ChecksPage } from "./pages/Checks";
import { HealthPage } from "./pages/Health";
import { ServicesPage } from "./pages/Services";
import { NewProject } from "./pages/NewProject";
import { OverviewPage } from "./pages/Overview";
import { People } from "./pages/People";
import { ProjectPage } from "./pages/Project";
import { Projects } from "./pages/Projects";
import { SignIn } from "./pages/SignIn";
import { SystemPage } from "./pages/System";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/auth/me") });
}

export function useProjects() {
  return useQuery({ queryKey: ["projects"], queryFn: () => api<ProjectSummary[]>("/projects") });
}

/** Jump to a project by name, slug or client. "/" focuses it. */
function Search() {
  const projects = useProjects();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const hits = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return [];
    return (projects.data ?? [])
      .filter((p) => [p.name, p.slug, p.client].some((s) => s.toLowerCase().includes(n)))
      .slice(0, 6);
  }, [q, projects.data]);

  const go = (slug: string) => {
    setQ("");
    setOpen(false);
    input.current?.blur();
    nav(`/p/${slug}`);
  };

  return (
    <div className="search">
      <Icon name="search" size={14} />
      <input
        ref={input}
        aria-label="Find a project"
        placeholder="Find a project, client or slug"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setSel(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
          if (e.key === "Enter" && hits[sel]) go(hits[sel].slug);
          if (e.key === "Escape") input.current?.blur();
        }}
      />
      <kbd>/</kbd>
      {open && hits.length > 0 && (
        <div className="results" role="listbox">
          {hits.map((p, i) => (
            <a key={p.id} href={`/p/${p.slug}`} role="option" aria-selected={i === sel} onMouseDown={(e) => { e.preventDefault(); go(p.slug); }}>
              <span>{p.name}</span>
              <span className="muted small">{p.client || p.slug}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function App() {
  const me = useMe();
  const projects = useProjects();
  const alerts = useAlerts();
  const qc = useQueryClient();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [loc.pathname]);

  if (me.isPending) return <div className="signin muted">Loading…</div>;
  if (me.error instanceof ApiError && me.error.status === 401) return <SignIn />;
  if (me.error) return <div className="signin error">{me.error.message}</div>;
  const user = me.data;

  const signOut = async () => {
    await api("/auth/logout", { method: "POST" });
    qc.clear();
    window.location.assign("/");
  };

  return (
    <div className="app">
      <aside className={`sidebar${menu ? " open" : ""}`} aria-label="Lab navigation">
        <div className="brand">
          <Mark />
          <div>
            SCO Lab
            <small>525K Supply Chain Optimization</small>
          </div>
          <button className="btn ghost menu-toggle" style={{ marginLeft: "auto" }} onClick={() => setMenu(false)} aria-label="Close menu">
            <Icon name="close" />
          </button>
        </div>
        <NavLink to="/" end className="nav-item"><Icon name="overview" />Overview</NavLink>
        <NavLink to="/projects" className="nav-item">
          <Icon name="projects" />Projects
          {projects.data && <span className="count">{projects.data.length}</span>}
        </NavLink>
        <NavLink to="/alerts" className="nav-item">
          <Icon name="alert" />Alerts
          {alerts.data && <span className="count">{alerts.data.filter((a) => a.severity !== "info").length}</span>}
        </NavLink>
        <NavLink to="/system" className="nav-item"><Icon name="system" />System</NavLink>
        <NavLink to="/docs" className="nav-item"><Icon name="docs" />Docs</NavLink>
        <NavLink to="/services" className="nav-item"><Icon name="services" />Shared services</NavLink>
        <NavLink to="/costs" className="nav-item"><Icon name="costs" />Costs</NavLink>
        {can(user, "dev") && <NavLink to="/new" className="nav-item"><Icon name="plus" />New project</NavLink>}
        <div className="nav-group">Team</div>
        <NavLink to="/people" className="nav-item"><Icon name="people" />People</NavLink>
        <NavLink to="/checks" className="nav-item"><Icon name="checks" />Checks</NavLink>
        <NavLink to="/health" className="nav-item"><Icon name="health" />Lab health</NavLink>
        {can(user, "admin") && <NavLink to="/audit" className="nav-item"><Icon name="audit" />Audit log</NavLink>}
        <div className="sidebar-foot">
          <Person name={user.displayName} avatarUrl={user.avatarUrl} />
          <div className="who">
            <div>{user.displayName}</div>
            <div className="small muted">{user.role}</div>
          </div>
          <button className="btn ghost small" onClick={signOut}>Sign out</button>
        </div>
      </aside>

      <header className="topbar">
        <button className="btn ghost menu-toggle" onClick={() => setMenu(true)} aria-label="Open menu"><Icon name="menu" /></button>
        <Search />
        <span className="menu-toggle" style={{ alignItems: "center", gap: 8 }}><Mark size={20} /> SCO Lab</span>
      </header>

      <main className="sheet">
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/costs" element={<CostsPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/new" element={can(user, "dev") ? <NewProject /> : <Navigate to="/" />} />
          <Route path="/p/:slug" element={<ProjectPage />} />
          <Route path="/people" element={<People />} />
          <Route path="/checks" element={<ChecksPage />} />
          <Route path="/health" element={<HealthPage />} />
          <Route path="/audit" element={can(user, "admin") ? <Audit /> : <Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}
